import { isObject } from 'underscore';

import { EmailTypeDisplayNames, IScheduledEmail, IScheduledEmailCustomizations } from '@/types/email.types';
import { BrandliveEvent } from '@/types/projects.types';
import { LanguagesAbbr } from '@/types/translation.types';
import TranslateLangsMap from '@/utils/translate-langs-map.json';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

export function getPublishedEmailLanguages(
  email: IScheduledEmail,
  workingProject: BrandliveEvent,
  emailLanguage?: LanguagesAbbr
): LanguagesAbbr[] {
  if (emailLanguage) {
    return [emailLanguage];
  }

  const session = email.session_uuid
    ? workingProject.sessions.find((session) => session.uuid === email.session_uuid)
    : undefined;

  if (session) {
    return session.languages as LanguagesAbbr[];
  }

  return Array.from(new Set(workingProject.sessions.flatMap((session) => session.languages as LanguagesAbbr[])));
}

function getLanguageLabels(languages: LanguagesAbbr[]): string {
  return languages.map((language) => (TranslateLangsMap as Record<string, string>)[language] ?? language).join(', ');
}

function getDeliveryDate(email: IScheduledEmail): string | null {
  if (!email.delivery_date) {
    return null;
  }

  const scheduledEmailDate = new Date(email.delivery_date);
  if (isNaN(scheduledEmailDate.getTime())) {
    return null;
  }

  const date = scheduledEmailDate.toLocaleString(navigator.language, {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });
  const time = scheduledEmailDate.toLocaleTimeString(navigator.language, {
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
  });
  return `${date} ${time}`;
}

function describeEmail(email: IScheduledEmail): string {
  const emailTypeDisplayName = EmailTypeDisplayNames[email.email_type] ?? email.email_type;
  const deliveryDate = getDeliveryDate(email);
  return deliveryDate ? `${emailTypeDisplayName} email scheduled for ${deliveryDate}` : `${emailTypeDisplayName} email`;
}

function showEmailContentToast(description: string): void {
  toastTrigger('Some email content could not be updated', {
    type: EToastTypes.error,
    autoClose: 1000000,
    description,
  });
}

/**
 * Warns the admin when a published scheduled email did not end up with content for every
 * language it will be sent in.
 */
export function alertOnIncompleteEmailContent({
  email,
  workingProject,
  emailLanguage,
  emailContent,
}: {
  email: IScheduledEmail;
  workingProject: BrandliveEvent;
  emailLanguage?: LanguagesAbbr;
  emailContent: IScheduledEmailCustomizations;
}): void {
  if (!isObject(emailContent) || !Object.keys(emailContent).length) {
    console.error('No email content found for email: ', email);
    showEmailContentToast(
      `Your ${describeEmail(email)} has no content to send. Please refresh your page, review your email content and re-publish. If the issue persists, delete and re-create the email.`
    );
    return;
  }

  const missingLanguages = getPublishedEmailLanguages(email, workingProject, emailLanguage).filter(
    (language) => !Object.keys(emailContent).includes(language)
  );

  if (!missingLanguages.length) {
    return;
  }

  console.error('Missing email content for languages: ', missingLanguages, email);
  showEmailContentToast(
    `Your ${describeEmail(email)} is missing content for ${getLanguageLabels(missingLanguages)}. Please refresh your page, review your email content and re-publish. If the issue persists, delete and re-create the email.`
  );
}
